import axiosInstance from "./axiosInstance";

export const getUser = async () => {
  const res = await axiosInstance.get("/user", {
    headers: {
      "Content-Type": "application/json",
    },
  });

  return res.data;
};

export const signin = async ({ email, password }) => {
  const res = await axiosInstance.post(
    "/user/signin",
    { email, password },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const signup = async ({ name, email, password }) => {
  const res = await axiosInstance.post(
    "/user/signup",
    { name, email, password },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const getUsers = async ({ role, enabled, search } = {}) => {
  const params = {};

  if (role) params.role = role;
  if (enabled !== undefined && enabled !== "") params.enabled = enabled;
  if (search) params.search = search;

  const res = await axiosInstance.get("/user/all", { params });
  return res.data;
};

export const adminCreateUser = async ({ name, email, password, role }) => {
  const res = await axiosInstance.post(
    "/user/admin-create",
    { name, email, password, role },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const updateUserRole = async (id, role) => {
  const res = await axiosInstance.put(
    `/user/${id}/role`,
    { role },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const updateUserEnabled = async (id, enabled) => {
  const res = await axiosInstance.put(
    `/user/${id}/enabled`,
    { enabled },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const deleteUser = async (id) => {
  const res = await axiosInstance.delete(`/user/${id}`);
  return res.data;
};

export const getCameras = async (
  page,
  limit,
  filters = {},
  sortBy,
  sortOrder
) => {
  const params = {
    page,
    limit,
    filter: filters,
  };

  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;

  const res = await axiosInstance.get("/camera", { params });
  // Backend now returns { data, total, page, limit }; unwrap to the array for
  // legacy callers (dropdowns etc.). New tables use listCameras for the envelope.
  return Array.isArray(res.data) ? res.data : res.data?.data ?? [];
};

export const getCamera = async (id) => {
  const res = await axiosInstance.get(`/camera/${id}`);
  return res.data;
};

// Edge360 device discovery — cameras on the requester's current network, matched
// by shared public IP (server-side). Optional ssid narrows shared-public-IP shops.
export const discoverCameras = async (ssid) => {
  const res = await axiosInstance.get("/camera/discover", { params: { ssid: ssid || undefined } });
  return res.data; // { publicIp, windowMins, count, data: [...] }
};

export const getRecentStats = async (id) => {
  const res = await axiosInstance.get(`/stats/${id}?limit=1`);

  if (res.data.length === 0) {
    return null;
  }
  return res.data[0];
};

export const getStats = async (id, from = null, to = null, limit = 10) => {
  const params = {
    limit,
  };

  if (from) params.from = from;
  if (to) params.to = to;

  const res = await axiosInstance.get(`/stats/${id}`, { params });
  return res.data;
};

export const getConfigs = async (
  page,
  limit,
  filters = {},
  sortBy,
  sortOrder
) => {
  const params = {
    page,
    limit,
    filter: filters,
  };

  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;

  const res = await axiosInstance.get("/config", { params });
  // Unwrap the { data, total } envelope for legacy callers; tables use listConfigs.
  return Array.isArray(res.data) ? res.data : res.data?.data ?? [];
};

export const getConfig = async (id) => {
  const res = await axiosInstance.get(`/config/${id}`);
  return res.data;
};

export const updateConfig = async (id, data) => {
  const res = await axiosInstance.put(`/config/${id}`, data, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return res.data;
};

export const updateCameraConfig = async (cameraId, configId) => {
  const res = await axiosInstance.put(
    `/camera/${cameraId}`,
    { config: configId },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const updateCameraShop = async (cameraId, shopId) => {
  const res = await axiosInstance.put(`/camera/${cameraId}`, { shop: shopId || null });
  return res.data;
};

// Set the per-device preventive-maintenance interval (null=default, 0=disabled, N=days).
export const updateCameraPreventive = async (cameraId, intervalDays) => {
  const res = await axiosInstance.put(`/camera/${cameraId}`, { preventiveIntervalDays: intervalDays });
  return res.data;
};

export const runPreventiveSweep = async () => {
  const res = await axiosInstance.post("/visits/preventive/run", {});
  return res.data;
};

// --- Edge360 onboarding (D2 new-install activation) ---
export const registerDevice = async (payload) => {
  // POST /camera is an upsert by deviceId; returns the (populated) camera.
  const res = await axiosInstance.post("/camera", payload);
  return res.data;
};

export const getDeviceByDeviceId = async (deviceId) => {
  const res = await axiosInstance.get(`/camera/device-id/${deviceId}`);
  return res.data;
};

export const bindDeviceConfigShop = async (deviceId, { config, shop } = {}) => {
  const res = await axiosInstance.put(`/camera/device-id/${deviceId}`, { config, shop });
  return res.data;
};

// ---------------------------------------------------------------------------
// Door-cycle analytics. deviceId is OPTIONAL on every read — omit for fleet-wide
// results, pass it to scope to one device, just like the from/to date filter.
// ---------------------------------------------------------------------------
const doorCycleParams = ({ deviceId, from, to } = {}) => ({
  deviceId: deviceId || undefined,
  from: from || undefined,
  to: to || undefined,
});

export const getDoorCycleAnalytics = async (opts = {}) => {
  const res = await axiosInstance.get("/door-cycle/analytics", { params: doorCycleParams(opts) });
  return res.data;
};

// ---------------------------------------------------------------------------
// Frame quality — how much of what the cameras captured was actually usable, and
// where the rest fell out. Same optional deviceId/from/to convention as above;
// `threshold` overrides the chiller gate cut-off (server default 0.70).
// ---------------------------------------------------------------------------
const frameQualityParams = (opts = {}) => ({
  ...doorCycleParams(opts),
  threshold: opts.threshold ?? undefined,
});

export const getFrameQualityFunnel = async (opts = {}) => {
  const res = await axiosInstance.get("/frame-quality/funnel", { params: frameQualityParams(opts) });
  return res.data;
};

export const getFrameQualityTimeseries = async (opts = {}) => {
  const res = await axiosInstance.get("/frame-quality/timeseries", {
    params: { ...frameQualityParams(opts), type: opts.type || "daily" },
  });
  return res.data;
};

export const getFrameQualityDevices = async (opts = {}) => {
  const res = await axiosInstance.get("/frame-quality/devices", { params: frameQualityParams(opts) });
  return res.data;
};

// Detection health — one consolidated, plain-language payload for the decision dashboard:
// the chiller usable/barely/nothing split, the person kept/blurred/whole-frame split, fleet
// health tiers, a named worst-cameras list with a recommended action, the signals the
// "what to do now" list is built from, and a 14-day usable-vs-redaction trend. Same optional
// deviceId/from/to/threshold convention; `minFrames` sets the "busy enough to judge" floor.
export const getDetectionHealthOverview = async (opts = {}) => {
  const res = await axiosInstance.get("/detection-health/overview", {
    params: { ...frameQualityParams(opts), minFrames: opts.minFrames ?? undefined },
  });
  return res.data;
};

// Threshold bench: re-score ONE stored photo and get every raw number back. Costs a real
// inference on the server (~0.4s), so call it on an explicit user action — never on a timer.
export const getFrameQualityBench = async (pictureId) => {
  const res = await axiosInstance.get("/frame-quality/bench", { params: { pictureId } });
  return res.data;
};

// The saved thresholds that production actually runs on. These used to be environment
// variables across three stacks; they now live in DMS so the bench's sliders are the real
// control rather than a simulation.
export const getThresholds = async () => {
  const res = await axiosInstance.get("/frame-quality/thresholds");
  return res.data;
};

// Partial update — send only what changed. hardwareAdmin+ only. Takes effect on the next
// frame ingested, with no redeploy.
export const saveThresholds = async (patch) => {
  const res = await axiosInstance.put("/frame-quality/thresholds", patch);
  return res.data;
};

// O124 what-if sandbox: replay the REAL selection engine over a batch (or device+range) under
// trial thresholds merged onto the live settings, and get back each frame's would-be verdict and
// the would-be winner. Read-only — production is untouched. Body: { batchId } OR { deviceId,
// from?, to?, limit? }, plus { thresholds: { ...trial values } }.
export const simulateSelection = async (body) => {
  const res = await axiosInstance.post("/picture/compliance/simulate", body);
  return res.data;
};

// Live Selection Monitor aggregate: totals, per-day counts, SKU distribution, gate drop-off and
// the recent-selections feed for the last `days` days. Read-only; any logged-in user.
export const getSelectionMonitor = async (days = 7) => {
  const res = await axiosInstance.get("/picture/compliance/monitor", { params: { days } });
  return res.data;
};

// --- Human-detection review study (super-admin only) --------------------------------------
// A temporary, opt-in observer that keeps PRE-REDACTION originals for a few days so the person
// detector can be judged against real photos. All of these are gated to superAdmin on the
// server, and the image bytes are served ONLY through the authed route below — hence the blob
// fetch rather than a plain <img src>.

// A page of recently captured detections (newest first). Optional deviceId filter.
export const getDetectionReview = async (opts = {}) => {
  const res = await axiosInstance.get("/detection-review", {
    params: {
      deviceId: opts.deviceId || undefined,
      // "true" = fires only, "false" = misses only, undefined = both.
      detected: opts.detected === undefined ? undefined : opts.detected,
      page: opts.page || undefined,
      limit: opts.limit || undefined,
    },
  });
  return res.data;
};

// The study's on/off state and retention window.
export const getDetectionReviewConfig = async () => {
  const res = await axiosInstance.get("/detection-review/config");
  return res.data;
};

// Start/stop the study or change retention. superAdmin only.
export const setDetectionReviewConfig = async (patch) => {
  const res = await axiosInstance.put("/detection-review/config", patch);
  return res.data;
};

// One preserved original, fetched WITH auth (an <img> tag can't send the token to the gated
// route). Returns an object URL the caller must revoke when done to avoid leaking blobs.
export const getDetectionReviewImageUrl = async (id) => {
  const res = await axiosInstance.get(`/detection-review/image/${id}`, {
    responseType: "blob",
  });
  return URL.createObjectURL(res.data);
};

// The photos one door-open produced, resolved to viewable URLs plus the chiller reading
// recorded for each at ingest. Costs a per-burst lookup, so it is fetched when a row is
// opened — never for the whole table.
export const getDoorCycleFrames = async (cycleId) => {
  const res = await axiosInstance.get(`/door-cycle/${cycleId}/frames`);
  return res.data;
};

export const getDoorCycleTimeseries = async (opts = {}) => {
  const res = await axiosInstance.get("/door-cycle/timeseries", {
    params: { ...doorCycleParams(opts), type: opts.type || "daily" },
  });
  return res.data;
};

export const getDoorCycleCycles = async (opts = {}) => {
  const res = await axiosInstance.get("/door-cycle/cycles", {
    params: { ...doorCycleParams(opts), page: opts.page || 1, limit: opts.limit || 20 },
  });
  return res.data;
};

// ---------------------------------------------------------------------------
// Compliance best-image selection — the frames the Planogram routine actually ran
// on (isSelected), each traceable to the batch/run it was chosen from. Same
// optional deviceId/from/to convention as the door-cycle endpoints.
// ---------------------------------------------------------------------------
export const getComplianceSelections = async (opts = {}) => {
  const res = await axiosInstance.get("/picture/compliance/selections", {
    params: { ...doorCycleParams(opts), batch: opts.batch || undefined, page: opts.page || 1, limit: opts.limit || 24 },
  });
  return res.data;
};

// The candidate frames a single selection run weighed, winner first.
export const getComplianceBatch = async (batchId) => {
  const res = await axiosInstance.get(`/picture/compliance/batch/${batchId}`);
  return res.data;
};

// Manual override (O109): force one frame from a no-selection batch back into compliance.
// hardwareAdmin+ only (enforced server-side). The frame is queued for delivery on the device's
// next forCompliance pull.
export const selectBatchFrame = async (batchId, pictureId) => {
  const res = await axiosInstance.post(`/picture/compliance/batch/${batchId}/select`, { pictureId });
  return res.data;
};

// Runs that weighed photos but selected NO winner (nothing cleared the chiller gate).
export const getComplianceNoSelection = async (opts = {}) => {
  const res = await axiosInstance.get("/picture/compliance/no-selection", {
    params: { ...doorCycleParams(opts), batch: opts.batch || undefined, page: opts.page || 1, limit: opts.limit || 24 },
  });
  return res.data;
};

// Borderline person-in-crop calls to audit: frames whose in-crop person score is near the
// exclusion threshold, so the team can see whether the "person" is real or model noise. O106.
export const getComplianceReview = async (opts = {}) => {
  const res = await axiosInstance.get("/picture/compliance/review", {
    params: { ...doorCycleParams(opts), batch: opts.batch || undefined, page: opts.page || 1, limit: opts.limit || 24 },
  });
  return res.data;
};

// Per device × per day rollup for the Excel export: daily photos captured, distinct
// compliance batches run, and frames delivered to Planogram — bucketed by local (PK) day.
// Respects the same optional deviceId/from/to window as the selections feed.
export const getComplianceDailyReport = async (opts = {}) => {
  const res = await axiosInstance.get("/picture/compliance/daily-report", {
    params: doorCycleParams(opts),
  });
  return res.data;
};

// ---------------------------------------------------------------------------
// F2 — Shops / Outlets
// ---------------------------------------------------------------------------
export const listShops = async ({ page, pageSize, search, sortField, sortOrder, city, region, channel, isActive } = {}) => {
  const res = await axiosInstance.get("/shops", {
    params: {
      page,
      limit: pageSize,
      filter: search || undefined,
      city: city || undefined,
      region: region || undefined,
      channel: channel || undefined,
      isActive: isActive === undefined ? undefined : isActive,
      sortBy: sortField || undefined,
      sortOrder: sortOrder || undefined,
    },
  });
  return res.data; // { data, total, page, limit }
};

export const getShopFacets = async () => {
  const res = await axiosInstance.get("/shops/facets");
  return res.data; // { cities, regions, channels }
};

export const getShop = async (id) => {
  const res = await axiosInstance.get(`/shops/${id}`);
  return res.data;
};

export const createShop = async (payload) => {
  const res = await axiosInstance.post("/shops", payload);
  return res.data;
};

export const updateShop = async (id, payload) => {
  const res = await axiosInstance.put(`/shops/${id}`, payload);
  return res.data;
};

export const deleteShop = async (id) => {
  const res = await axiosInstance.delete(`/shops/${id}`);
  return res.data;
};

export const assignDeviceToShop = async (shopId, { cameraId, deviceId }) => {
  const res = await axiosInstance.post(`/shops/${shopId}/devices`, { cameraId, deviceId });
  return res.data;
};

export const unassignDeviceFromShop = async (shopId, cameraId) => {
  const res = await axiosInstance.delete(`/shops/${shopId}/devices/${cameraId}`);
  return res.data;
};

export const identifyShop = async (code) => {
  const res = await axiosInstance.get(`/shops/identify`, { params: { code } });
  return res.data;
};

export const searchShops = async (search, limit = 50) => {
  // Server-side search for the outlet picker (the catalog can be tens of thousands).
  const res = await axiosInstance.get("/shops", {
    params: { page: 1, limit, filter: search || undefined },
  });
  return res.data?.data || [];
};

export const syncShopsFromSource = async (prefix) => {
  const res = await axiosInstance.post("/shops/sync", prefix ? { prefix } : {});
  return res.data; // { fetched, created, updated, skipped, source }
};

// ---------------------------------------------------------------------------
// D2 — Technician visits + checklist templates
// ---------------------------------------------------------------------------
export const listVisits = async ({ page, pageSize, search, sortField, sortOrder, status, visitType, mine, unassigned, overdue, deviceId, shop } = {}) => {
  const res = await axiosInstance.get("/visits", {
    params: {
      page,
      limit: pageSize,
      filter: search || undefined,
      status: status || undefined,
      visitType: visitType || undefined,
      mine: mine ? true : undefined,
      unassigned: unassigned ? true : undefined,
      overdue: overdue ? true : undefined,
      deviceId: deviceId || undefined,
      shop: shop || undefined,
    },
  });
  return res.data; // { data, total, page, limit }
};

export const getVisit = async (id) => {
  const res = await axiosInstance.get(`/visits/${id}`);
  return res.data;
};

export const createVisit = async (payload) => {
  const res = await axiosInstance.post("/visits", payload);
  return res.data;
};

export const updateVisit = async (id, payload) => {
  const res = await axiosInstance.put(`/visits/${id}`, payload);
  return res.data;
};

// Assign one technician across many visits (hardwareAdmin+). technician=null clears.
export const bulkAssignVisits = async (visitIds, technician) => {
  const res = await axiosInstance.post("/visits/bulk-assign", { visitIds, technician: technician || null });
  return res.data; // { assigned, skipped, notFound, technician, technicianName }
};

export const transitionVisit = async (id, action, note) => {
  const res = await axiosInstance.post(`/visits/${id}/transition`, { action, note });
  return res.data;
};

// D5 — upload a captured photo (Blob/File) to a visit.
export const uploadVisitPhoto = async (id, file, caption) => {
  const fd = new FormData();
  fd.append("photo", file, "photo.jpg");
  if (caption) fd.append("caption", caption);
  const res = await axiosInstance.post(`/visits/${id}/photos`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// D3 — supervisor verify + rate a completed visit.
export const verifyVisit = async (id, { rating, comment } = {}) => {
  const res = await axiosInstance.post(`/visits/${id}/verify`, { rating, comment });
  return res.data;
};

export const getTechnicianStats = async () => {
  const res = await axiosInstance.get("/visits/technician-stats");
  return res.data?.data || [];
};

// C5 — on-device commands (restart / snapshot)
export const enqueueDeviceCommand = async (deviceId, type, payload) => {
  const res = await axiosInstance.post("/commands", { deviceId, type, payload });
  return res.data;
};

export const listDeviceCommands = async (deviceId) => {
  const res = await axiosInstance.get("/commands", { params: { deviceId, limit: 10 } });
  return res.data?.data || [];
};

// On-demand device terminal (Phase 2). openTerminal is gated by the separate
// TERMINAL_PASSWORD on top of the admin JWT. Returns { sessionId, ttl, url }; `url`
// is a path under the API host — build the iframe src with the API origin.
export const openTerminal = async (deviceId, password) => {
  const res = await axiosInstance.post(`/terminal/${deviceId}/open`, { password });
  return res.data;
};
export const closeTerminal = async (deviceId, sessionId) => {
  const res = await axiosInstance.post(`/terminal/${deviceId}/close`, { sessionId });
  return res.data;
};
// Absolute terminal URL from the returned path (API origin + path). axiosInstance
// baseURL is like https://host/api; the path already starts with /api.
export const terminalUrl = (path) => {
  try { return new URL(path, axiosInstance.defaults.baseURL).href; } catch { return path; }
};

// Upload an agent release tarball; returns { filename, url, sha256, size } to
// queue as an update-agent command.
export const uploadAgentRelease = async (file) => {
  const form = new FormData();
  form.append("release", file);
  const res = await axiosInstance.post("/releases", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// ---- Fleet release rollout (desired-state channel) ----
// List uploaded releases with their channel (draft/canary/published) + rollout state.
export const listReleases = async () => {
  const res = await axiosInstance.get("/releases");
  return res.data?.data || [];
};

// Live rollout progress for a release: { stats: { total, onTarget, pending, failed, ... } }.
export const getReleaseRollout = async (filename) => {
  const res = await axiosInstance.get(`/releases/${encodeURIComponent(filename)}/rollout`);
  return res.data;
};

// Target a test-device set (canary) before going fleet-wide.
export const setReleaseCanary = async (filename, deviceIds) => {
  const res = await axiosInstance.post(`/releases/${encodeURIComponent(filename)}/canary`, { deviceIds });
  return res.data;
};

// Promote a release to the whole fleet ("make public"). Supersedes the previous one.
export const publishRelease = async (filename) => {
  const res = await axiosInstance.post(`/releases/${encodeURIComponent(filename)}/publish`, {});
  return res.data;
};

export const pauseReleaseRollout = async (filename, reason) => {
  const res = await axiosInstance.post(`/releases/${encodeURIComponent(filename)}/pause`, { reason });
  return res.data;
};

export const resumeReleaseRollout = async (filename) => {
  const res = await axiosInstance.post(`/releases/${encodeURIComponent(filename)}/resume`, {});
  return res.data;
};

// ---- Android app distribution (APK upload + public download page) ----
// Upload an .apk; the server parses versionName/versionCode from the manifest and
// makes it the latest build. Returns the stored AndroidApp record.
export const uploadApk = async (file, notes) => {
  const form = new FormData();
  form.append("apk", file);
  if (notes) form.append("notes", notes);
  const res = await axiosInstance.post("/android-app", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// All published builds, newest first (public endpoint).
export const listApks = async () => {
  const res = await axiosInstance.get("/android-app");
  return res.data?.data || [];
};

// The build the public download page offers by default (public endpoint).
export const getLatestApk = async () => {
  const res = await axiosInstance.get("/android-app/latest");
  return res.data;
};

// Promote a build to be the public default.
export const setApkLatest = async (filename) => {
  const res = await axiosInstance.post(`/android-app/${encodeURIComponent(filename)}/latest`, {});
  return res.data;
};

// Edit a build's release notes.
export const updateApk = async (filename, payload) => {
  const res = await axiosInstance.patch(`/android-app/${encodeURIComponent(filename)}`, payload);
  return res.data;
};

export const deleteApk = async (filename) => {
  const res = await axiosInstance.delete(`/android-app/${encodeURIComponent(filename)}`);
  return res.data;
};

export const listChecklistTemplates = async (visitType) => {
  const res = await axiosInstance.get("/checklist-templates", {
    params: { visitType: visitType || undefined, isActive: true },
  });
  return res.data?.data || [];
};

// Full list (incl. inactive) for the template-builder admin page.
export const listAllChecklistTemplates = async () => {
  const res = await axiosInstance.get("/checklist-templates");
  return res.data?.data || [];
};

export const getChecklistTemplate = async (id) => {
  const res = await axiosInstance.get(`/checklist-templates/${id}`);
  return res.data;
};

export const createChecklistTemplate = async (payload) => {
  const res = await axiosInstance.post("/checklist-templates", payload);
  return res.data;
};

export const updateChecklistTemplate = async (id, payload) => {
  const res = await axiosInstance.put(`/checklist-templates/${id}`, payload);
  return res.data;
};

export const deleteChecklistTemplate = async (id) => {
  const res = await axiosInstance.delete(`/checklist-templates/${id}`);
  return res.data;
};

export const getPictures = async (deviceId, limit, qr) => {
  const params = {};

  if (limit) params.limit = limit;
  if (qr) params.qr = qr;

  const res = await axiosInstance.get(`/picture/${deviceId}`, { params });
  return res.data;
};

export const getLogs = async (deviceId, limit) => {
  const params = {};

  if (limit) params.limit = limit;

  const res = await axiosInstance.get(`/logs/${deviceId}`, { params });
  return res.data.logs;
};

export const getLogsFile = async (deviceId, filename) => {
  const res = await axiosInstance.get(`/logs/${deviceId}/${filename}`);
  return res.data;
};

// ---------------------------------------------------------------------------
// Revamp: paginated list wrappers. Each accepts the DataTable fetcher params
// ({ page, pageSize, search, sortField, sortOrder, ...filters }) and resolves
// to the backend envelope { data, total, page, limit }.
// ---------------------------------------------------------------------------
// `archived`: "false" (default, live fleet) | "true" (the archive) | "all".
export const listCameras = async ({
  page,
  pageSize,
  search,
  sortField,
  sortOrder,
  archived,
  registeredBefore,
} = {}) => {
  const res = await axiosInstance.get("/camera", {
    params: {
      page,
      limit: pageSize,
      filter: search || undefined,
      sortBy: sortField || undefined,
      sortOrder: sortOrder || undefined,
      archived: archived || undefined,
      registeredBefore: registeredBefore || undefined,
    },
  });
  return res.data;
};

// ---- Device archive (soft delete) ----
// Archive a device set. Pass ids (Mongo _id) and/or deviceIds, and/or
// registeredBefore to sweep a whole registration era (e.g. everything from the
// test run that preceded the prod golden image). `dryRun` previews the match
// without changing anything. hardwareAdmin+ only.
export const archiveDevices = async ({ ids, deviceIds, registeredBefore, reason, dryRun } = {}) => {
  const res = await axiosInstance.post("/camera/archive", {
    ids,
    deviceIds,
    registeredBefore,
    reason,
    dryRun,
  });
  return res.data; // { matched, archived, devices }
};

// Restore archived devices to the live fleet. Explicit selection only.
export const unarchiveDevices = async ({ ids, deviceIds } = {}) => {
  const res = await axiosInstance.post("/camera/unarchive", { ids, deviceIds });
  return res.data; // { matched, restored, devices }
};

export const listConfigs = async ({ page, pageSize, search, sortField, sortOrder } = {}) => {
  const res = await axiosInstance.get("/config", {
    params: {
      page,
      limit: pageSize,
      filter: search || undefined,
      sortBy: sortField || undefined,
      sortOrder: sortOrder || undefined,
    },
  });
  return res.data;
};

export const listPictures = async (deviceId, { page, pageSize, qr, from, to } = {}) => {
  const res = await axiosInstance.get(`/picture/${deviceId}`, {
    params: { page, limit: pageSize, qr: qr || undefined, from, to },
  });
  return res.data;
};

export const listLogs = async (deviceId, { page, pageSize, search } = {}) => {
  const res = await axiosInstance.get(`/logs/${deviceId}`, {
    params: { page, limit: pageSize, search: search || undefined },
  });
  return res.data;
};


// ---- Fleet Health (C1) ----

export const getFleetHealth = async ({
  state,
  color,
  search,
  lifecycle,
  remoteFixable,
} = {}) => {
  const params = {};
  if (state) params.state = state;
  if (color) params.color = color;
  if (search) params.search = search;
  if (lifecycle) params.lifecycle = lifecycle;
  if (remoteFixable !== undefined && remoteFixable !== null) {
    params.remoteFixable = String(remoteFixable);
  }

  const res = await axiosInstance.get("/health", { params });
  return res.data;
};

// ---- Field-ops reports (hardwareAdmin+) ------------------------------------
// Store-wise list of every OFFLINE device with the outlet's coordinates — the daily
// MR-supervisor field worklist. Returns { generatedAt, count, missingCoordinates, rows }.
export const getOfflineDevicesReport = async () => {
  const res = await axiosInstance.get("/reports/offline-devices");
  return res.data;
};

// Last-image freshness across the deployed fleet. Returns { generatedAt, summary, devices }
// where summary = { totalDeployed, within24h, over24h, over48h, neverCaptured }.
export const getImageFreshnessReport = async () => {
  const res = await axiosInstance.get("/reports/image-freshness");
  return res.data;
};

// Bulk raise + assign OFFLINE tickets for the selected offline devices. Reassigns an
// existing open ticket where one exists, otherwise creates a new OFFLINE ticket, then
// assigns all to `assignedTo`. Returns { total, created, reassigned, assignedToName }.
export const assignOfflineTickets = async (deviceIds, assignedTo) => {
  const res = await axiosInstance.post("/reports/offline-tickets", { deviceIds, assignedTo });
  return res.data;
};

// The pool of users an OFFLINE ticket can be assigned to. Supervisors own the field
// queue, but there are 0 supervisor accounts today, so technicians must be selectable
// too. Returns [{ value: _id, label: `${name} · ${role}` }].
export const getAssignableFieldUsers = async () => {
  const [sup, tech] = await Promise.all([
    getUsers({ role: "supervisor", enabled: true }),
    getUsers({ role: "technician", enabled: true }),
  ]);
  const rows = [...(sup?.users || []), ...(tech?.users || [])];
  return rows
    .filter((u) => u && u._id)
    .map((u) => ({ value: u._id, label: `${u.name} · ${u.role}` }));
};

// ---- Settings → Device Scope (hardwareAdmin+) -----------------------------
// The operator-defined criteria that decide which devices are still in our own
// facility, and therefore stay out of the fleet counts.

export const getDeviceScopeSettings = async () => {
  const res = await axiosInstance.get("/settings/device-scope");
  return res.data;
};

export const saveDeviceScopeSettings = async (inHouseRules, matchMode = "any") => {
  const res = await axiosInstance.put("/settings/device-scope", { inHouseRules, matchMode });
  return res.data;
};

// Dry-run a rule set: what would it classify, and which devices would move?
export const previewDeviceScopeSettings = async (inHouseRules, matchMode = "any") => {
  const res = await axiosInstance.post("/settings/device-scope/preview", {
    inHouseRules,
    matchMode,
  });
  return res.data;
};

// The classification worklist — every active device, how it is classified and why.
export const getDeviceClassification = async ({ lifecycle, search } = {}) => {
  const params = {};
  if (lifecycle) params.lifecycle = lifecycle;
  if (search) params.search = search;
  const res = await axiosInstance.get("/settings/device-scope/devices", { params });
  return res.data;
};

// Pin a device's lifecycle by hand (hardwareAdmin+). `value: null` releases the pin
// and returns the device to automatic classification by the health sweep.
export const setCameraLifecycle = async (cameraId, { value, reason } = {}) => {
  const res = await axiosInstance.put(`/camera/${cameraId}/lifecycle`, {
    value: value ?? null,
    reason,
  });
  return res.data;
};

// Replace a device's operator tags (hardwareAdmin+). Descriptive only — tags never
// change how a device is reported.
export const setCameraTags = async (cameraId, tags) => {
  const res = await axiosInstance.put(`/camera/${cameraId}/tags`, { tags });
  return res.data;
};

export const getDeviceHealth = async (deviceId) => {
  const res = await axiosInstance.get(`/health/${deviceId}`);
  return res.data;
};

export const getAlerts = async ({ status, type, deviceId } = {}) => {
  const params = {};
  if (status) params.status = status;
  if (type) params.type = type;
  if (deviceId) params.deviceId = deviceId;

  const res = await axiosInstance.get("/alerts", { params });
  return res.data;
};

export const resolveAlert = async (id) => {
  const res = await axiosInstance.put(`/alerts/${id}/resolve`);
  return res.data;
};

export const getAlertRules = async () => {
  const res = await axiosInstance.get("/alert-rules");
  return res.data;
};

export const updateGlobalAlertRule = async (body) => {
  const res = await axiosInstance.put("/alert-rules/global", body, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return res.data;
};

export const updateDeviceAlertRule = async (deviceId, body) => {
  const res = await axiosInstance.put(`/alert-rules/device/${deviceId}`, body, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return res.data;
};

// ---- Tickets (C3) ----

export const getTickets = async ({
  status,
  priority,
  deviceId,
  type,
  assignedTo,
  mine,
  unassigned,
} = {}) => {
  const params = {};
  if (status) params.status = status;
  if (priority) params.priority = priority;
  if (deviceId) params.deviceId = deviceId;
  if (type) params.type = type;
  if (assignedTo) params.assignedTo = assignedTo;
  if (mine) params.mine = true;
  if (unassigned) params.unassigned = true;

  const res = await axiosInstance.get("/tickets", { params });
  return res.data;
};

export const getTicket = async (id) => {
  const res = await axiosInstance.get(`/tickets/${id}`);
  return res.data;
};

export const createTicket = async (body) => {
  const res = await axiosInstance.post("/tickets", body, {
    headers: {
      "Content-Type": "application/json",
    },
  });
  return res.data;
};

export const createTicketFromAlert = async (alertId) => {
  const res = await axiosInstance.post(`/tickets/from-alert/${alertId}`);
  return res.data;
};

export const assignTicket = async (id, assignedTo) => {
  const res = await axiosInstance.put(
    `/tickets/${id}/assign`,
    { assignedTo },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const setTicketStatus = async (id, status, note) => {
  const res = await axiosInstance.put(
    `/tickets/${id}/status`,
    { status, note },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

export const addTicketNote = async (id, text) => {
  const res = await axiosInstance.post(
    `/tickets/${id}/notes`,
    { text },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return res.data;
};

// ---- Footfall (door-open) analytics ----------------------------------------
// Bucketed door-open report. `type` = hourly | daily | weekly | monthly.
// Each row also carries plannedCount / unplannedCount (door-open duration below
// / at-or-above durationThresholdSec) for the Planned vs Unplanned donut.
export const getFootfallReport = async ({
  type = "daily",
  from = null,
  to = null,
  limit = null,
  deviceIds = null,
  durationThresholdSec = null,
} = {}) => {
  const params = { type };
  if (from) params.from = from;
  if (to) params.to = to;
  if (limit) params.limit = limit;
  if (deviceIds && deviceIds.length) params.deviceIds = JSON.stringify(deviceIds);
  if (durationThresholdSec) params.durationThresholdSec = durationThresholdSec;

  const res = await axiosInstance.get("/footfall/report", { params });
  return res.data; // { type, durationThresholdSec, data: [{ period, range, count, plannedCount, unplannedCount }] }
};

// Hour-by-hour rows spanning a from/to window (for multi-day hourly views).
export const getFootfallHourlyDays = async ({
  from,
  to,
  deviceIds = null,
  durationThresholdSec = null,
} = {}) => {
  const params = { from, to };
  if (deviceIds && deviceIds.length) params.deviceIds = JSON.stringify(deviceIds);
  if (durationThresholdSec) params.durationThresholdSec = durationThresholdSec;

  const res = await axiosInstance.get("/footfall/report/hourly-days", { params });
  return res.data; // { durationThresholdSec, data: [{ period, range, count, plannedCount, unplannedCount }] }
};

// ---------------------------------------------------------------------------------------
// Model Bench — compare person detectors on a frozen, uploaded image set.
//
// The Threshold Bench asks "where should the bar sit for the model we run?". These answer
// the question underneath it: "is this the model we should be running?" — which production
// frames alone cannot settle, because every candidate has to see the SAME pixels for the
// comparison to mean anything.
// ---------------------------------------------------------------------------------------

// What the engine can serve, what the operator has pinned, and — the only one that is
// evidence — the model recorded on the most recently screened production frame.
export const getHumanModels = async () => {
  const res = await axiosInstance.get("/human-eval/models");
  return res.data;
};

export const getEvalDatasets = async () => {
  const res = await axiosInstance.get("/human-eval/datasets");
  return res.data;
};

export const createEvalDataset = async (body) => {
  const res = await axiosInstance.post("/human-eval/datasets", body);
  return res.data;
};

// Upload once; the set is then permanent, and every model evaluated afterwards sees exactly
// these pixels. Duplicates are collapsed by content hash server-side and reported back
// rather than rejected — re-uploading a folder should say what was already in it.
export const uploadEvalImages = async (datasetId, files, label, onProgress) => {
  const form = new FormData();
  for (const f of files) form.append("images", f);
  if (label) form.append("label", label);
  const res = await axiosInstance.post(`/human-eval/datasets/${datasetId}/images`, form, {
    onUploadProgress: (e) =>
      onProgress && e.total && onProgress(Math.round((e.loaded / e.total) * 100)),
  });
  return res.data;
};

export const getEvalImages = async (datasetId, label) => {
  const res = await axiosInstance.get(`/human-eval/datasets/${datasetId}/images`, {
    params: label ? { label } : {},
  });
  return res.data;
};

// The ground truth every printed number is downstream of. hardwareAdmin+.
export const labelEvalImage = async (imageId, label) => {
  const res = await axiosInstance.patch(`/human-eval/images/${imageId}`, { label });
  return res.data;
};

export const deleteEvalImage = async (imageId) => {
  const res = await axiosInstance.delete(`/human-eval/images/${imageId}`);
  return res.data;
};

// Auth-gated pixels: these frames are un-redacted people held as ground truth for a privacy
// control, so they are NOT in the public uploads tree. Fetched as a blob and turned into an
// object URL, because an <img src> cannot carry the Authorization header.
export const fetchEvalImageBlob = async (imageId) => {
  const res = await axiosInstance.get(`/human-eval/images/${imageId}/file`, {
    responseType: "blob",
  });
  return URL.createObjectURL(res.data);
};

// Costs real inference on the CPU engine that is also screening live uploads, so it returns
// immediately with a run id and the caller polls. Already-scored pairs are skipped.
export const startEvalRun = async (body) => {
  const res = await axiosInstance.post("/human-eval/runs", body);
  return res.data;
};

export const getEvalRun = async (runId) => {
  const res = await axiosInstance.get(`/human-eval/runs/${runId}`);
  return res.data;
};

export const cancelEvalRun = async (runId) => {
  const res = await axiosInstance.post(`/human-eval/runs/${runId}/cancel`);
  return res.data;
};

// The whole score matrix, UNTHRESHOLDED. Every box and score comes down once so the
// threshold slider sweeps the full range in the browser with no round trip — the same trick
// the Threshold Bench uses, and for the same reason: scoring per position would mean
// sampling a few thresholds by hand, and what separates these models lives between the
// points a person would pick.
export const getEvalResults = async (datasetId, models) => {
  const res = await axiosInstance.get(`/human-eval/datasets/${datasetId}/results`, {
    params: models && models.length ? { models: models.join(",") } : {},
  });
  return res.data;
};

// Pin the model ingest screens with. Applied on the very next upload; null hands the choice
// back to the engine's own default. hardwareAdmin+.
export const promoteHumanModel = async (modelId) => {
  const res = await axiosInstance.put("/human-eval/promote", { modelId });
  return res.data;
};

// ---- SKU Lab (O110) ------------------------------------------------------------------
// Unthresholded detection on an ad-hoc upload. The point of the page is to show what the
// confidence floor removes, so the default call asks the engine for everything it has.

// The models the serving registry can actually run. Read from the engine rather than held
// as a list here: a picker built from a committed list drifts the moment a model is
// registered or rolled back, and then fails per image instead of at the point it went stale.
export const getSkuLabModels = async () => {
  const res = await axiosInstance.get("/sku-lab/models");
  return res.data;
};

// One image, one model, every box. `conf` is sent explicitly (including 0-ish values) so the
// caller can walk the floor down and watch detections appear.
export const runSkuLabDetect = async ({ file, modelId, conf, iou }) => {
  const form = new FormData();
  form.append("image", file);
  form.append("modelId", modelId);
  if (conf !== undefined && conf !== null) form.append("conf", String(conf));
  if (iou !== undefined && iou !== null) form.append("iou", String(iou));
  const res = await axiosInstance.post("/sku-lab/detect", form, {
    headers: { "Content-Type": "multipart/form-data" },
    // A full-resolution frame at a near-zero floor on the CPU-only engine is slow by nature;
    // the default client timeout would abort a run that was about to succeed.
    timeout: 180000,
  });
  return res.data;
};
